import {
  PAYMENT__BILLING_CADENCE,
  PAYMENT__MENTORING_TYPE
} from '@algoacademy/backend/modules/payment/enums'
import { z } from 'zod'

/**
 * What the mutation did, in the words the form needs to say it: `scheduled` - a
 * change now waits for the renewal; `takenBack` - the change that was waiting is
 * gone and today's terms simply continue; `unchanged` - staff submitted terms
 * already in force or already waiting for renewal, which is not an error and
 * records nothing.
 */
export const CHANGE_MENTORING_SUBSCRIPTION_OUTCOME = {
  SCHEDULED: 'scheduled',
  TAKEN_BACK: 'takenBack',
  UNCHANGED: 'unchanged'
} as const

export type ChangeMentoringSubscriptionOutcome =
  (typeof CHANGE_MENTORING_SUBSCRIPTION_OUTCOME)[keyof typeof CHANGE_MENTORING_SUBSCRIPTION_OUTCOME]

const TermsZ = z.object({
  mentoringType: z.enum(Object.values(PAYMENT__MENTORING_TYPE)),
  mentoringTypeLabel: z.string(),
  billingCadence: z.enum(Object.values(PAYMENT__BILLING_CADENCE)),
  billingCadenceLabel: z.string(),
  /** Only individual mentoring carries hours; other types leave it null. */
  hoursWithMentor: z.number().int().nullable()
})

export const ChangeMentoringSubscriptionOutputDTOZ = z.object({
  /** The subscription the change was filed against. */
  subscriptionId: z.uuid(),
  outcome: z.enum(Object.values(CHANGE_MENTORING_SUBSCRIPTION_OUTCOME)),
  /** The terms being billed today; unchanged by this mutation. */
  current: TermsZ,
  /** The renewal date - when a waiting change takes effect. It never moves. */
  renewsAt: z.date(),
  /** Existing waiting change on an unchanged resubmission; otherwise the new change or null. */
  scheduledChange: TermsZ.extend({
    /** Frozen from the price book at this moment; a later change never moves it. */
    priceGrosze: z.number().int(),
    effectiveAt: z.date()
  }).nullable()
})

export type ChangeMentoringSubscriptionOutputDTO = z.infer<
  typeof ChangeMentoringSubscriptionOutputDTOZ
>
